/**
 * @file Rent charges — the §6.1 machine, timezone-aware period generation,
 * proration, lateness and the late-fee default (T1.4).
 *
 * Five things live here, and they are together because they are one decision seen
 * from five angles: *what does this lease owe for this period, and what happens to
 * that obligation over time?*
 *
 *   1. **The §6.1 states and the transition map, as data.** The map records the
 *      edges the plan draws and nothing else, exactly as `leases.ts` does for
 *      §6.2 — a chain of `if`s in a route cannot be enumerated, and a new state
 *      would be a silent omission rather than a compile error.
 *   2. **Period generation as a pure function** (`generateRentChargePeriods`).
 *      THE rule that dominates this task: a charge belongs to the **property's**
 *      timezone, never the server's. "The 1st of the month" means the 1st in the
 *      building's zone; in a Worker the process zone is UTC, so getting this
 *      wrong produces an off-by-one-day charge for every property in another
 *      zone — the normal case, not an edge case. The function takes facts and an
 *      instant and touches nothing, so the rule is testable with a zone that
 *      differs from the test runner's.
 *   3. **Proration** (`prorateRent`) for a period clipped to the tenancy. The
 *      METHOD is read from `workspaces.settings.proration` (§5). **No configured
 *      method means no proration** — the full rent is charged and the result says
 *      so. Inventing a default method would be a founder decision made by an
 *      implementer.
 *   4. **Lateness** (`evaluateRentChargeLate`): §6.1 — late when
 *      `due_date + grace_days` has elapsed AND the balance is unpaid, evaluated on
 *      the property's calendar.
 *   5. **The late FEE defaults to nothing** (`evaluateLateFee`). §6.1 puts the rule
 *      on the lease, makes it prompt-by-default, and makes it automatic only if
 *      the landlord enabled it. Every lease in the database has `late_fee = '{}'`,
 *      so the empty rule is the operative case: no fee, and no prompt either,
 *      because a prompt implies an amount the platform would have to invent.
 *
 * Money is INTEGER CENTS everywhere (CLAUDE.md / INV-MONEY-1) and never a float.
 * Proration can produce a fractional cent; that is resolved by ONE documented
 * rounding rule (`roundHalfUpDivide`) here, never by `Math.round` at a call site.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle,
 * no app code. The SPA imports these TYPES and the label map; the API is the
 * authority on what is valid.
 */
import { z } from "zod";
import type { LeaseStatus } from "./leases.js";

/**
 * The §6.1 rent-charge states, in the order PLAN.md §6.1 draws them.
 *
 * Mirrored by the `rent_charge_status` enum in
 * `packages/db/migrations/0007_rent_charges.sql`; a state added in one place and
 * not the other is refused by the enum at write time.
 */
export const RENT_CHARGE_STATUSES = [
  "scheduled",
  "due",
  "partially_paid",
  "paid",
  "late",
  "written_off",
] as const;

export type RentChargeStatus = (typeof RENT_CHARGE_STATUSES)[number];

/** Mutable-tuple form, for `z.enum` (mirrors LEASE_STATUS_VALUES). */
export const RENT_CHARGE_STATUS_VALUES = RENT_CHARGE_STATUSES.map(
  (status) => status,
) as [RentChargeStatus, ...RentChargeStatus[]];

/**
 * Human labels for the same six states.
 *
 * Here rather than in the SPA because two surfaces will render them (the Rent &
 * charges page now, the lease detail later), and `partially_paid` appearing raw
 * in a badge is how a status list starts disagreeing with itself.
 */
export const RENT_CHARGE_STATUS_LABELS: Readonly<Record<RentChargeStatus, string>> = {
  scheduled: "Scheduled",
  due: "Due",
  partially_paid: "Partially paid",
  paid: "Paid",
  late: "Late",
  written_off: "Written off",
};

/**
 * The states in which a balance is still owed — the set §6.1's "any-unpaid →
 * written_off" is defined over.
 *
 * `paid` is settled and `written_off` has released the debt (and is terminal), so
 * neither is unpaid. `scheduled` IS unpaid in the only sense that matters here:
 * the charge exists and no money has been applied to it yet, so a landlord must
 * be able to write it off (a tenancy cancelled before its first due date is the
 * real case). Exported so the written-off edges below and the tests cannot
 * disagree about which states they cover.
 */
export const UNPAID_RENT_CHARGE_STATUSES = [
  "scheduled",
  "due",
  "partially_paid",
  "late",
] as const;

/** Is this state one in which money is still owed? */
export function isRentChargeUnpaid(status: RentChargeStatus): boolean {
  return (UNPAID_RENT_CHARGE_STATUSES as readonly RentChargeStatus[]).includes(status);
}

/**
 * The §6.1 transition map, as data.
 *
 * Every state has an entry — `Record<RentChargeStatus, …>` is total, so adding a
 * state without saying where it can go fails to compile rather than silently
 * becoming unreachable. The edges are exactly the ones §6.1 draws:
 *
 *     scheduled → due → partially_paid → paid
 *     due | partially_paid → late
 *     late → partially_paid | paid
 *     any-unpaid → written_off                       (landlord action, audited)
 *
 * **The deliberately narrow reading, and why.** §6.1 draws the happy chain
 * THROUGH `partially_paid`, so `due → paid` is not an edge here even though a
 * tenant can obviously pay a due charge in full. Adding it would be implementing
 * a machine the plan does not specify; when the payment task (T1.5) needs it, that
 * is a plan change with a stated reason, not a local convenience. The same applies
 * to `scheduled → written_off`, which IS drawn — §6.1 says any UNPAID state, and a
 * scheduled charge is unpaid — and to `written_off` being terminal (nothing in
 * §6.1 leaves it).
 */
export const RENT_CHARGE_TRANSITIONS: Readonly<
  Record<RentChargeStatus, readonly RentChargeStatus[]>
> = {
  // Time moves a charge out of `scheduled` and into `due`; the transition route
  // is the only writer, and it audits the move.
  scheduled: ["due", "written_off"],
  due: ["partially_paid", "late", "written_off"],
  partially_paid: ["paid", "late", "written_off"],
  late: ["partially_paid", "paid", "written_off"],
  // Terminal: a paid charge has no balance, so there is nothing to write off, and
  // §6.1 draws no edge that leaves this state.
  paid: [],
  // Terminal: the debt was released by an audited landlord action. A charge that
  // turns out to have been collected after all is a correction (a ledger event),
  // not a state move — the same reasoning as the append-only ledger.
  written_off: [],
};

/** May a charge move from `from` to `to`? The §6.1 map, as one question. */
export function canTransitionRentCharge(
  from: RentChargeStatus,
  to: RentChargeStatus,
): boolean {
  return RENT_CHARGE_TRANSITIONS[from].includes(to);
}

/** Every state a charge in `from` may move to — for a UI or an error message. */
export function nextRentChargeStatuses(from: RentChargeStatus): readonly RentChargeStatus[] {
  return RENT_CHARGE_TRANSITIONS[from];
}

/**
 * `status_transitions.entity_type` for a rent charge.
 *
 * One spelling, exported, so the writer and the test that reads the audit row
 * cannot disagree about it.
 */
export const RENT_CHARGE_ENTITY_TYPE = "rent_charge";

// ---------------------------------------------------------------------------
// Calendar arithmetic — pure, timezone-free
// ---------------------------------------------------------------------------

/**
 * Every date in this module is a `YYYY-MM-DD` calendar date with no zone attached
 * — which is the point. A period is a range of LOCAL days in the property's
 * zone, and turning it back into an instant would reintroduce the off-by-one this
 * module exists to prevent. Comparisons are therefore string comparisons, which
 * are correct for this format.
 */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Days from the Unix epoch to a civil date. TZ-free: it is pure calendar math. */
const MS_PER_DAY = 86_400_000;

interface CivilDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

/** Days in a Gregorian month. `month` is 1–12. */
export function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one. Date.UTC touches no
  // local zone, so the answer cannot depend on where this runs.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function parseIsoDate(value: string): CivilDate {
  const match = ISO_DATE_PATTERN.exec(value);
  if (match === null) {
    throw new Error(`rent-charges: "${value}" is not a YYYY-MM-DD date`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`rent-charges: "${value}" is not a real calendar date`);
  }
  return { year, month, day };
}

function formatIsoDate(civil: CivilDate): string {
  return `${pad(civil.year, 4)}-${pad(civil.month, 2)}-${pad(civil.day, 2)}`;
}

/** Days since the epoch for a civil date. */
function dayNumber(civil: CivilDate): number {
  return Date.UTC(civil.year, civil.month - 1, civil.day) / MS_PER_DAY;
}

function isoDayNumber(value: string): number {
  return dayNumber(parseIsoDate(value));
}

/** `a` before `b`, after `b`, or the same day — the only comparison needed. */
function compareIso(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Move a calendar date by whole days. */
export function addDaysToDate(value: string, days: number): string {
  const moved = new Date((isoDayNumber(value) + days) * MS_PER_DAY);
  return formatIsoDate({
    year: moved.getUTCFullYear(),
    month: moved.getUTCMonth() + 1,
    day: moved.getUTCDate(),
  });
}

/** Inclusive day count: the same day is 1, consecutive days are 2. */
export function inclusiveDays(start: string, end: string): number {
  return isoDayNumber(end) - isoDayNumber(start) + 1;
}

/** Next calendar month, as a `{year, month}` pair. */
function nextMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/** Previous calendar month, as a `{year, month}` pair. */
function previousMonth(year: number, month: number): { year: number; month: number } {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

/**
 * The billing date in a given month.
 *
 * `billing_day` is 1–31 (the CHECK in 0005 mirrors the zod schema), and a month
 * can be shorter than 31 days, so the day is clamped to the last day of the
 * month. That is a data-shape resolution, not product policy: a lease billed on
 * the 31st is billed on Feb 28, and skipping February's charge entirely would be
 * a far worse answer than the obvious one.
 */
function billingDateInMonth(year: number, month: number, billingDay: number): string {
  return formatIsoDate({ year, month, day: Math.min(billingDay, daysInMonth(year, month)) });
}

/**
 * The local calendar date at an instant, in a named IANA zone.
 *
 * **This is the function the whole task turns on.** `Intl` with an explicit
 * `timeZone` is the only zone database available in both Node and a Worker, and
 * reading the year/month/day parts off it gives the building's day rather than the
 * process's. `new Date().toISOString().slice(0, 10)` — the tempting one-liner —
 * answers in UTC, which is wrong for every property east or west of it for part
 * of every day.
 *
 * The zone is validated where it is written (`isIanaTimeZone` in `properties.ts`
 * refuses `EST`, a fixed UTC-5 offset that is wrong for eight months of the
 * year), so an invalid value here is a stored-data bug and `Intl` throwing a
 * RangeError is the correct, loud outcome.
 */
export function localDateInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);

  let year: string | undefined;
  let month: string | undefined;
  let day: string | undefined;
  for (const part of parts) {
    if (part.type === "year") year = part.value;
    else if (part.type === "month") month = part.value;
    else if (part.type === "day") day = part.value;
  }
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(
      `rent-charges: could not read a calendar date in "${timeZone}" — the zone is not recognised`,
    );
  }
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// Period generation (§6.4)
// ---------------------------------------------------------------------------

/**
 * §6.4's `N`: a charge is materialized this many days BEFORE its due date.
 *
 * It is why the generator runs ahead of the calendar at all — a landlord sees next
 * month's charge before it is due — and it is half of the catch-up rule: a period
 * should exist once its generation date (due date − N) has passed, whether or not
 * the cron was running at the time.
 */
export const CHARGE_GENERATION_LEAD_DAYS = 10;

/**
 * One period a charge should exist for.
 *
 * `periodStart`/`periodEnd` are what the charge COVERS, clipped to the tenancy at
 * each end. `cycleStart`/`cycleEnd` are the full billing cycle it belongs to, and
 * exist because proration needs a denominator: a tenant who moved in on Aug 15
 * occupies 17 of August's 31 days, and the August cycle is not visible from a
 * clipped period alone.
 */
export interface RentChargePeriod {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly dueDate: string;
  readonly cycleStart: string;
  readonly cycleEnd: string;
}

/** The facts period generation reads. Facts, not rows — so it needs no database. */
export interface RentChargeGenerationFacts {
  /** The tenancy's first day, `YYYY-MM-DD`, in the property's zone. */
  readonly startDate: string;
  /** The tenancy's last day, inclusive, `YYYY-MM-DD`, in the property's zone. */
  readonly endDate: string;
  /** Day of the month rent falls due, 1–31 (the lease's `billing_day`). */
  readonly billingDay: number;
  /** The PROPERTY's IANA zone. Never the server's. */
  readonly timeZone: string;
  /** Override for §6.4's lead time. Defaults to `CHARGE_GENERATION_LEAD_DAYS`. */
  readonly leadDays?: number;
}

/**
 * **The periods a lease's charges should exist for** (PLAN.md §6.4).
 *
 * Pure and hermetic: facts plus an instant in, a list of calendar periods out. No
 * database, no clock, no process timezone — which is what makes the timezone rule
 * testable.
 *
 * The periods tile `[startDate, endDate]` with no gaps and no overlaps:
 *
 *   * The **anchor** is the first billing date on or after the lease start. If the
 *     tenancy starts on the billing day (the ordinary case: the 1st), the anchor
 *     IS the start and the first period is a full cycle.
 *   * Otherwise the first period is a **stub** running from the move-in day to the
 *     day before the anchor — so a tenant who moves in on the 15th of a month
 *     billed on the 1st is charged for the 15th to the end of that month, and
 *     nothing is charged for days nobody occupied.
 *   * Each subsequent period is its billing cycle, with the last one ending at the
 *     tenancy's end date.
 *   * The **due date is the period's start** — rent is due when the period it pays
 *     for begins — so a move-in stub is due on the move-in day.
 *
 * A period is only returned once its GENERATION date (due date − `leadDays`) has
 * arrived, compared on the PROPERTY's calendar. That single rule is what makes the
 * job both forward-looking and catching-up: a lease recorded today with six months
 * of history has six generation dates in the past, so all six periods come back at
 * once, while a period due next month is held back until the lead time is reached.
 *
 * A lease whose end precedes its start generates nothing — a data bug the route
 * will reject, and one this must not turn into a negative-length charge.
 */
export function generateRentChargePeriods(
  facts: RentChargeGenerationFacts,
  now: Date,
): readonly RentChargePeriod[] {
  const { startDate, endDate, billingDay, timeZone } = facts;
  const leadDays = facts.leadDays ?? CHARGE_GENERATION_LEAD_DAYS;

  if (!Number.isInteger(billingDay) || billingDay < 1 || billingDay > 31) {
    throw new Error(
      `rent-charges: billingDay must be a whole number between 1 and 31, received ${String(billingDay)}`,
    );
  }
  if (!Number.isInteger(leadDays) || leadDays < 0) {
    throw new Error(
      `rent-charges: leadDays must be a whole number >= 0, received ${String(leadDays)}`,
    );
  }
  // Parsed for validation as well as arithmetic: a bad date must not silently
  // produce an empty list.
  const start = parseIsoDate(startDate);
  parseIsoDate(endDate);
  const timeZoneDay = localDateInZone(now, timeZone);

  if (compareIso(endDate, startDate) < 0) return [];

  const periods: RentChargePeriod[] = [];

  // The anchor: the first billing date on or after the tenancy's first day.
  const billingInStartMonth = billingDateInMonth(start.year, start.month, billingDay);
  const anchorIsThisMonth = compareIso(billingInStartMonth, startDate) >= 0;
  const anchorMonth = anchorIsThisMonth ? start : nextMonth(start.year, start.month);
  const anchor = billingDateInMonth(anchorMonth.year, anchorMonth.month, billingDay);

  if (compareIso(startDate, anchor) < 0) {
    // The stub: move-in day to the day before the anchor, part-way through the
    // cycle that precedes the anchor.
    const stubEnd = addDaysToDate(anchor, -1);
    const priorMonth = previousMonth(anchorMonth.year, anchorMonth.month);
    periods.push({
      periodStart: startDate,
      periodEnd: compareIso(stubEnd, endDate) < 0 ? stubEnd : endDate,
      dueDate: startDate,
      cycleStart: billingDateInMonth(priorMonth.year, priorMonth.month, billingDay),
      cycleEnd: stubEnd,
    });
  }

  // Then one period per billing cycle, until the tenancy ends. The loop advances
  // the month cursor, so it terminates on a finite end date; comparing the
  // billing day (not the clamped date) is what keeps a lease billed on the 31st
  // billing on the 31st again after a short month.
  let cursor = anchorMonth;
  for (;;) {
    const cycleStart = billingDateInMonth(cursor.year, cursor.month, billingDay);
    if (compareIso(cycleStart, endDate) > 0) break;

    const following = nextMonth(cursor.year, cursor.month);
    const cycleEnd = addDaysToDate(
      billingDateInMonth(following.year, following.month, billingDay),
      -1,
    );

    periods.push({
      periodStart: cycleStart,
      periodEnd: compareIso(cycleEnd, endDate) < 0 ? cycleEnd : endDate,
      dueDate: cycleStart,
      cycleStart,
      cycleEnd,
    });

    cursor = following;
  }

  // The catch-up filter. `dueDate - leadDays <= today`, on the property's
  // calendar: everything whose generation date has passed exists exactly once,
  // however long the cron was asleep.
  return periods.filter(
    (period) =>
      compareIso(addDaysToDate(period.dueDate, -leadDays), timeZoneDay) <= 0,
  );
}

// ---------------------------------------------------------------------------
// Proration
// ---------------------------------------------------------------------------

/**
 * The proration methods PLAN.md §6.4 names: "actual-days or 30-day".
 *
 * Read from `workspaces.settings.proration` (§5). The list is the source of truth
 * for what the platform implements: `readProrationMethod` accepts exactly these,
 * and the Account & team settings surface (T2.12) offers exactly these — it reads
 * this constant through `PRORATION_METHOD_OPTIONS` rather than re-listing them, so
 * the picker cannot offer a method the generator would silently ignore.
 *
 * Writable since the Account & team settings route landed: `PATCH
 * /workspaces/:id/settings` is the first writer of the setting, so the absent case
 * is now a landlord's choice ("not configured") rather than the only state a
 * workspace could be in.
 */
export const PRORATION_METHODS = ["actual_days", "30_day"] as const;
export type ProrationMethod = (typeof PRORATION_METHODS)[number];

/**
 * How each method is named to a landlord.
 *
 * A `Record<ProrationMethod, string>`, so a method added to the list above fails
 * to compile until it has a label — a picker that showed a raw `30_day` would be
 * the enum leaking into the product. The wording says what the method DOES rather
 * than what the enum is called: §6.4's two options differ only in the denominator.
 */
export const PRORATION_METHOD_LABELS: Readonly<Record<ProrationMethod, string>> = {
  actual_days: "Actual days — divide the rent by the real length of the month",
  "30_day": "30-day month — divide the rent by 30, whatever the month's length",
};

/**
 * The honest name for the state a workspace is in when no method is configured.
 *
 * Exported so the settings surface, the API payload and any future screen say the
 * same thing about the same state, rather than each inventing a phrase for "no
 * proration".
 */
export const PRORATION_UNCONFIGURED_LABEL = "Not configured — no proration";

/** The `workspaces.settings` key the method is read from. */
export const PRORATION_SETTING_KEY = "proration";

function isProrationMethod(value: unknown): value is ProrationMethod {
  return (
    typeof value === "string" && (PRORATION_METHODS as readonly string[]).includes(value)
  );
}

/**
 * The configured proration method, or `null` when none is configured.
 *
 * `null` is a real answer and the caller must treat it as one — see
 * `prorateRent`. Two shapes are accepted (`"actual_days"` and
 * `{ method: "actual_days" }`) because §5 names the setting without fixing its
 * shape and no code writes it yet; when a settings form lands it should pick one
 * and this tolerance should narrow. Anything unrecognised reads as absent, which
 * is the fail-safe direction: an unknown setting must not silently prorate.
 */
export function readProrationMethod(settings: unknown): ProrationMethod | null {
  if (settings === null || typeof settings !== "object") return null;
  const raw = (settings as Record<string, unknown>)[PRORATION_SETTING_KEY];
  if (isProrationMethod(raw)) return raw;
  if (raw !== null && typeof raw === "object") {
    const method = (raw as Record<string, unknown>)["method"];
    if (isProrationMethod(method)) return method;
  }
  return null;
}

/** Why a period is charged in full when no method is configured. */
export const NO_PRORATION_METHOD_REASON =
  "no proration method is configured in workspaces.settings.proration, so the " +
  "full period rent is charged — no default method is invented";

/**
 * **Integer half-up division, and the ONLY rounding rule for proration.**
 *
 * A prorated amount is `rent × occupiedDays ÷ denominatorDays`, which is
 * routinely fractional. The rule is documented here, once, so it is not sprinkled
 * through call sites as `Math.round`: **ties round up** (toward the landlord),
 * which is the ordinary commercial convention and the least surprising answer.
 * It is a mechanical tie-break, not a product policy — changing it changes every
 * prorated figure in the product, which is why it has exactly one home.
 *
 * Implemented with integer arithmetic (`Math.floor((2n + d) / 2d)`) rather than
 * `n / d` followed by rounding, because the float division is the thing that
 * loses the cent in the first place.
 */
export function roundHalfUpDivide(numerator: number, denominator: number): number {
  if (!Number.isInteger(numerator) || numerator < 0) {
    throw new Error(
      `rent-charges: proration numerator must be a whole, non-negative number of cents, received ${String(numerator)}`,
    );
  }
  if (!Number.isInteger(denominator) || denominator <= 0) {
    throw new Error(
      `rent-charges: proration denominator must be a whole number of days > 0, received ${String(denominator)}`,
    );
  }
  const scaled = numerator * 2 + denominator;
  if (!Number.isSafeInteger(scaled)) {
    throw new Error("rent-charges: proration would overflow integer cents");
  }
  return Math.floor(scaled / (denominator * 2));
}

/** The facts proration reads. Facts, not rows. */
export interface ProrationFacts {
  /** The workspace's configured method, or `null` (see `readProrationMethod`). */
  readonly method: ProrationMethod | null;
  /** The lease's rent for a FULL period, in integer cents. */
  readonly fullRentCents: number;
  /** The full billing cycle the period belongs to (the denominator's basis). */
  readonly cycleStart: string;
  readonly cycleEnd: string;
  /** The days actually charged — the period, clipped to the tenancy. */
  readonly occupiedStart: string;
  readonly occupiedEnd: string;
}

export interface ProrationResult {
  /** Integer cents. Equal to `fullRentCents` when no method is configured. */
  readonly amountCents: number;
  /** Did a partial period actually get prorated? */
  readonly prorated: boolean;
  readonly method: ProrationMethod | null;
  readonly occupiedDays: number;
  readonly denominatorDays: number;
  /** One sentence saying which rule produced this number, for a log or a UI. */
  readonly reason: string;
}

/**
 * What one period costs, proration applied per the workspace's method.
 *
 * Three outcomes, and the middle one is the founder-owned case:
 *
 *   * The period covers the whole cycle → the full rent, exactly, whatever the
 *     method. (`30_day` must not charge 31/30 of a month's rent.)
 *   * A partial period AND a configured method → prorated, by `actual_days`
 *     (denominator = the cycle's real length) or `30_day` (denominator = 30, the
 *     commercial month). Rounded by `roundHalfUpDivide`.
 *   * A partial period AND **no configured method** → the full rent. This is the
 *     deliberate answer to "what do we do when the workspace has not decided?": do
 *     nothing clever. Charging a fraction would require picking a convention the
 *     landlord never chose, and the result says so in `reason`.
 */
export function prorateRent(facts: ProrationFacts): ProrationResult {
  const cycleDays = inclusiveDays(facts.cycleStart, facts.cycleEnd);
  // Clamped: a caller whose range runs past its own cycle is describing the whole
  // cycle, and charging for days outside it would be over-charging a tenant.
  const rawOccupiedDays = inclusiveDays(facts.occupiedStart, facts.occupiedEnd);
  const occupiedDays = Math.max(0, Math.min(rawOccupiedDays, cycleDays));

  if (occupiedDays >= cycleDays) {
    return {
      amountCents: facts.fullRentCents,
      prorated: false,
      method: facts.method,
      occupiedDays: cycleDays,
      denominatorDays: cycleDays,
      reason: "the charge covers the whole billing period, so the full rent applies",
    };
  }

  if (facts.method === null) {
    return {
      amountCents: facts.fullRentCents,
      prorated: false,
      method: null,
      occupiedDays,
      denominatorDays: cycleDays,
      reason: NO_PRORATION_METHOD_REASON,
    };
  }

  const denominatorDays = facts.method === "actual_days" ? cycleDays : 30;
  const amountCents = roundHalfUpDivide(facts.fullRentCents * occupiedDays, denominatorDays);

  return {
    amountCents,
    prorated: true,
    method: facts.method,
    occupiedDays,
    denominatorDays,
    reason:
      facts.method === "actual_days"
        ? `prorated over ${String(occupiedDays)} of the cycle's ${String(cycleDays)} days (actual days)`
        : `prorated over ${String(occupiedDays)} days against a 30-day month`,
  };
}

// ---------------------------------------------------------------------------
// Lateness (§6.1)
// ---------------------------------------------------------------------------

/** The facts §6.1's lateness test reads. Facts, not rows. */
export interface RentChargeLateFacts {
  readonly dueDate: string;
  /** The LEASE's `grace_days` — not the platform's, and not a default. */
  readonly graceDays: number;
  /** What is still owed on this charge, in integer cents. 0 means settled. */
  readonly unpaidCents: number;
  readonly now: Date;
  /** The PROPERTY's IANA zone — the day lateness is decided on. */
  readonly timeZone: string;
}

export interface RentChargeLateEvaluation {
  readonly late: boolean;
  /** `due_date + grace_days` — the last day that is not late. */
  readonly graceEndsOn: string;
  /** Whole days from the due date to today, in the property's zone. Negative before it is due. */
  readonly daysSinceDue: number;
  readonly reason: string;
}

/**
 * **§6.1's lateness test**: `due_date|partially_paid → late` when
 * `due_date + grace_days` has elapsed AND the balance is unpaid.
 *
 * Both halves are load-bearing. Without the balance half, a tenant who paid would
 * be late anyway; without the grace half, "late" would mean "due".
 *
 * The grace period must have ELAPSED, so the grace day itself is not late: due
 * Jul 1 with 5 grace days is late from Jul 7, not Jul 6. And "today" is the day in
 * the PROPERTY's zone — a charge due Jul 1 in Detroit does not become late because
 * the server's UTC clock has already rolled over.
 */
export function evaluateRentChargeLate(
  facts: RentChargeLateFacts,
): RentChargeLateEvaluation {
  if (!Number.isInteger(facts.graceDays) || facts.graceDays < 0) {
    throw new Error(
      `rent-charges: graceDays must be a whole number >= 0, received ${String(facts.graceDays)}`,
    );
  }
  const graceEndsOn = addDaysToDate(facts.dueDate, facts.graceDays);
  const today = localDateInZone(facts.now, facts.timeZone);
  const daysSinceDue = isoDayNumber(today) - isoDayNumber(facts.dueDate);

  if (facts.unpaidCents <= 0) {
    return {
      late: false,
      graceEndsOn,
      daysSinceDue,
      reason: "nothing is outstanding on this charge",
    };
  }
  if (compareIso(today, graceEndsOn) <= 0) {
    return {
      late: false,
      graceEndsOn,
      daysSinceDue,
      reason: `due_date + grace_days has not elapsed (it ends ${graceEndsOn})`,
    };
  }
  return {
    late: true,
    graceEndsOn,
    daysSinceDue,
    reason: "due_date + grace_days has elapsed and the balance is unpaid (§6.1)",
  };
}

// ---------------------------------------------------------------------------
// Late fees — prompt by default, and nothing when the rule is empty
// ---------------------------------------------------------------------------

/** The rule key a configured fee amount is read from, in integer cents. */
export const LATE_FEE_AMOUNT_KEY = "amountCents";

/** The rule key a landlord sets to make the fee automatic instead of prompted. */
export const LATE_FEE_AUTOMATIC_KEY = "automatic";

/** Why the empty rule produces nothing at all. */
export const NO_LATE_FEE_RULE_REASON =
  "no late-fee rule is configured on this lease (late_fee is an empty rule), so " +
  "there is no fee to apply and no amount to prompt with — the platform does not " +
  "invent one";

export interface LateFeeEvaluation {
  /** Integer cents, or `null` when there is no fee to apply. Never invented. */
  readonly feeCents: number | null;
  /** Should the landlord be asked, one tap, to apply or waive it? */
  readonly prompt: boolean;
  /** May the fee be applied without asking? Only when the rule says so. */
  readonly automatic: boolean;
  readonly reason: string;
}

/**
 * **§6.1's late-fee rule, evaluated.** The rule lives on the lease (`late_fee`),
 * it is PROMPT-BY-DEFAULT (one tap apply/waive), and it is automatic only if the
 * landlord enabled that.
 *
 * Every lease in the database has `late_fee = '{}'`, so the empty rule is the case
 * that actually runs, and it produces **nothing**: no fee, and no prompt either. A
 * prompt would have to offer an amount, and the platform has not been told what
 * one is — so prompting would be the same invention as charging.
 *
 * Only an explicit integer `amountCents` is recognised. A percentage, a formula,
 * a tier table — none of those are read, because applying one would mean choosing
 * a base (the rent? the balance? the period?) that nobody has chosen. A rule this
 * function does not understand is reported as "no fee", never approximated.
 *
 * Applying or waiving a fee is a landlord decision recorded as a ledger event
 * (T1.3 gave the ledger its append surface); a waiver is NOT a state move and NOT
 * a deletion. Nothing in T1.4 writes one.
 */
export function evaluateLateFee(rule: unknown): LateFeeEvaluation {
  const nothing: LateFeeEvaluation = {
    feeCents: null,
    prompt: false,
    automatic: false,
    reason: NO_LATE_FEE_RULE_REASON,
  };

  if (rule === null || typeof rule !== "object") return nothing;

  const record = rule as Record<string, unknown>;
  const amount = record[LATE_FEE_AMOUNT_KEY];
  if (typeof amount !== "number" || !Number.isInteger(amount) || amount <= 0) {
    return nothing;
  }

  const automatic = record[LATE_FEE_AUTOMATIC_KEY] === true;
  return {
    feeCents: amount,
    automatic,
    // The platform asks unless the landlord has explicitly turned that off.
    prompt: !automatic,
    reason: automatic
      ? "the lease's rule enables the fee automatically, so the landlord is not prompted"
      : "the lease's rule is prompt-by-default: the landlord applies or waives it in one tap",
  };
}

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/**
 * POST /workspaces/:workspaceId/rent-charges/:chargeId/transitions body.
 *
 * `{ to }` and nothing else. `status` is never accepted on a create (there is no
 * create route — the generator owns the rows), and an amount is never accepted
 * anywhere: what a period costs is derived from the lease's rent and the
 * workspace's proration setting, never asserted by a client.
 */
export const rentChargeTransitionSchema = z.object({
  to: z.enum(RENT_CHARGE_STATUS_VALUES),
});

export type RentChargeTransitionInput = z.infer<typeof rentChargeTransitionSchema>;

/** The PRE-parse shape, for the SPA to build a request body from. */
export type RentChargeTransitionBody = z.input<typeof rentChargeTransitionSchema>;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One rent charge, as the API returns it.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages). Dates
 * are `YYYY-MM-DD` calendar dates in the property's zone — an instant would lose
 * the very thing they mean.
 */
export interface RentChargeDto {
  readonly id: string;
  readonly leaseId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly dueDate: string;
  /** Integer cents. */
  readonly amountCents: number;
  readonly status: RentChargeStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * The people on the lease a charge belongs to.
 *
 * Just enough to identify the household without a request per row; a charge is
 * owed by the lease, not by one person (the ledger is where attribution lives,
 * T1.3).
 */
export interface RentChargeTenantDto {
  readonly id: string;
  readonly fullName: string;
}

/**
 * A charge plus the context that makes it identifiable: the lease, the unit, the
 * property and the people. The portfolio-wide list answers "what does my
 * portfolio owe me, and when", so each row names where it came from without a
 * request per row — the same reasoning as `LeaseListRowDto`.
 */
export interface RentChargeListRowDto extends RentChargeDto {
  readonly leaseStatus: LeaseStatus;
  readonly graceDays: number;
  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  readonly tenants: readonly RentChargeTenantDto[];
}

/**
 * GET /workspaces/:workspaceId/rent-charges — every charge in the portfolio.
 *
 * `counts` and `byStatus` are folded from the rows returned beside them, so every
 * figure can be checked against the list. There is deliberately **no
 * `collectedCents`, no `outstandingCents` and no `overdueCents`**: those need the
 * ledger and payments (T1.5), and a rendered `$0 collected` reads as a fact about
 * the world rather than about the build. What the rows DO support — how many
 * charges exist and what each is for — is what is here.
 */
export interface RentChargeListDto {
  readonly counts: {
    readonly charges: number;
    /** Distinct leases with at least one charge — not the count of charges. */
    readonly leases: number;
  };
  /** Only the statuses that actually occur, in §6.1 order. */
  readonly byStatus: readonly {
    readonly status: RentChargeStatus;
    readonly charges: number;
  }[];
  /** One page (?limit=, ?cursor=), newest period first. `counts`/`byStatus` cover every charge. */
  readonly charges: readonly RentChargeListRowDto[];
  /** Pass as `?cursor=` for the next page; null on the last page. */
  readonly nextCursor: string | null;
}

/**
 * GET /workspaces/:workspaceId/leases/:leaseId/rent-charges — one lease's charges.
 *
 * `lease` is carried so a caller knows whether the generator is even allowed to
 * charge this tenancy: §6.4 materializes charges for `active` and
 * `month_to_month` leases only, and a draft lease having no charges is correct
 * rather than a bug.
 */
export interface LeaseRentChargesDto {
  readonly leaseId: string;
  readonly leaseStatus: LeaseStatus;
  readonly counts: {
    readonly charges: number;
  };
  readonly byStatus: readonly {
    readonly status: RentChargeStatus;
    readonly charges: number;
  }[];
  readonly charges: readonly RentChargeDto[];
}

/**
 * The result of running generation, whether from the job or on demand.
 *
 * `chargesCreated` and `chargesExisting` are reported separately and are exact:
 * the insert returns only the rows it actually wrote (`ON CONFLICT … DO NOTHING`),
 * so "existing" is the count of periods that were already there. Running twice
 * therefore reports `created: 0` — the evidence that the idempotency key works,
 * rather than a claim that it does.
 */
export interface RentChargeGenerationDto {
  readonly leasesScanned: number;
  readonly chargesCreated: number;
  readonly chargesExisting: number;
}
